import React, {useEffect, useMemo, useState} from 'react';

import {useDispatch, useSelector} from 'react-redux';
import {WithRouterProps} from 'react-router';

import {AccountPageShell} from 'app/account/components/AccountPageShell';
import NoResultsMessage from 'app/components/general/NoResultsMessage';
import {
  SelectDateRangeForm,
  VALID_RANGE_TEMPLATE,
} from 'app/general/widgets/SelectDateRangeForm';
import StateButton from 'app/general/widgets/StateButton';
import {userJobDispatcher} from 'app/jobs/userJobDispatcher';
import {useReportDetail, useRunReport} from 'app/reports/api';
import {getReportBySlugSelector} from 'app/reports/slice';
import {AppDispatchType} from 'app/store/AppStore';
import {useNotifications} from 'app/toasts/hooks';
import {gt} from 'app/utils/i18n';

interface Props extends WithRouterProps<{slug: string}, unknown> {
  item?: string;
}

export function ReportsDetailPage({params, router}: Props): JSX.Element {
  const [timeRange, setTimeRange] = useState<{end: string; start: string}>({
    start: '',
    end: '',
  });
  const dispatch = useDispatch<AppDispatchType>();
  const [runReport, {data: submittedJob, isError: isErrorSubmittingReport}] =
    useRunReport();
  const report = useSelector(getReportBySlugSelector(params.slug));
  const {error, isLoading} = useReportDetail(params.slug);
  const [isJobError, setJobError] = useState(false);
  const {showToastNotification} = useNotifications();
  const jobUnsubcribe = React.useRef<() => void>();

  useEffect(() => {
    if (submittedJob?.job != null && report != null) {
      const {job, unsubscribe} = dispatch(
        userJobDispatcher(Promise.resolve({job: submittedJob?.job}))
      );
      jobUnsubcribe.current = unsubscribe;

      job.then(
        (completedJob) => router.push(completedJob.result.url),
        () => setJobError(true)
      );
    }

    return () => jobUnsubcribe.current?.();
  }, [submittedJob?.job]);

  useEffect(() => {
    if (isErrorSubmittingReport) {
      showToastNotification(gt("Couldn't run the report at the moment"), {
        level: 'error',
      });
    }
  }, [isErrorSubmittingReport]);

  const label = useMemo(
    () =>
      report?.slug != null
        ? {
            'teammates-team-summary':
              'When a member joined or when they have used SweetProcess.',
            'list-documents': 'When the document was created.',
          }[report.slug]
        : undefined,
    [report?.slug]
  );

  return (
    <AccountPageShell
      location="reports"
      title={report?.name}
      content={report}
      error={error}
      isLoading={isLoading}
      errorContent={{head: gt('Could not load the report')}}
    >
      {() =>
        submittedJob?.job == null ? (
          <>
            <SelectDateRangeForm
              label={label}
              onChange={setTimeRange}
              initialValue="this-month"
              templates={availableTemplates}
              options={{position: 'below'}}
            />
            <div className="border-top mt-3 pt-4">
              <StateButton
                busyText={gt('Run Report')}
                className="btn btn-primary ml-auto"
                onClick={() =>
                  runReport({
                    starts_at: timeRange.start,
                    ends_at: timeRange.end,
                    slug: params.slug,
                  })
                }
              >
                {gt('Run Report')}
              </StateButton>
            </div>
          </>
        ) : isJobError ? (
          <NoResultsMessage
            icon="fa-exclamation-triangle text-warning"
            head={gt("Couldn't create the report")}
          >
            <p className="lead mb-0">
              {gt('There was an error creating your report.')}
            </p>
            <p className="lead">
              {gt('Our system has been notified of the problem.')}
            </p>
          </NoResultsMessage>
        ) : (
          <NoResultsMessage
            icon="fa-circle-notch fa-spin"
            head={gt('Generating report...')}
          >
            <p>{gt('This can take some minutes, you can continue working.')}</p>
            <p>{gt('We will notify you when your report is ready.')}</p>
          </NoResultsMessage>
        )
      }
    </AccountPageShell>
  );
}

const availableTemplates: Array<VALID_RANGE_TEMPLATE> = [
  'this-week',
  'this-month',
  'last-month',
  'this-quarter',
  'last-quarter',
  'this-year',
  'all-time',
  'custom',
];
